import "./stats.scss";
import { useEffect, useMemo, useState } from "react";
import { CircularProgress, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { MdOutlineInsights, MdRefresh } from "react-icons/md";
import { useGetOrg } from "hooks/useGetOrg";
import { urlReplaceState } from "helpers/url";
import { periods, readPeriodFromUrl } from "./periods";
import { formatPeriodRange } from "./dashboard-utils";
import {
  DashboardSection,
  DashboardWidget,
  SECTION_HINTS,
  SECTION_TITLES,
  getAvailableWidgets,
} from "./dashboard-widgets";
import OnboardingChecklist from "components/Onboarding/OnboardingChecklist";

const SECTIONS: DashboardSection[] = ["kpi", "charts", "tickets"];

/**
 * Дашборд собирается динамически из модулей организации (org.modules),
 * которые включаются в «Суперадмин → Организации → Модули».
 */
const StatsPage = () => {
  const { data: org, isLoading } = useGetOrg(localStorage.getItem("orgID"));
  // Период берём из URL, чтобы он переживал перезагрузку и им можно было поделиться ссылкой
  const [month, setMonth] = useState<number>(readPeriodFromUrl);

  const queryClient = useQueryClient();
  const isFetching = useIsFetching() > 0;

  const widgets = useMemo(() => getAvailableWidgets(org?.modules), [org?.modules]);
  const usesPeriod = widgets.some((w) => w.usesPeriod);

  const bySection = useMemo(() => {
    const map: Record<DashboardSection, DashboardWidget[]> = { kpi: [], charts: [], tickets: [] };
    widgets.forEach((w) => map[w.section].push(w));
    return map;
  }, [widgets]);

  useEffect(() => {
    if (usesPeriod) {
      urlReplaceState(null, { month });
    }
  }, [month, usesPeriod]);

  // Обновляем все активные запросы страницы, включая графики обращений
  const refresh = () => queryClient.invalidateQueries();

  if (isLoading) {
    return (
      <div>
        <Skeleton variant="text" width={200} height={40} />
        <div className="dashboard-grid dashboard-grid--kpi m-t-16">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="dashboard-grid__item">
              <Skeleton variant="rounded" height={76} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const visibleSections = SECTIONS.filter((s) => bySection[s].length > 0);

  return (
    <div className="dashboard">
      <OnboardingChecklist />
      <header className="dashboard__header">
        <div>
          <h5 className="dashboard__title">Аналитика</h5>
          {usesPeriod && <div className="dashboard__range">{formatPeriodRange(month)}</div>}
        </div>

        <div className="dashboard__controls">
          {usesPeriod && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={month}
              // null приходит при повторном клике по выбранному — игнорируем, период всегда выбран
              onChange={(_e, v) => v != null && setMonth(Number(v))}
              aria-label="Период"
            >
              {periods.map((p) => (
                <ToggleButton key={p.id} value={p.id} className="dashboard__period-btn">
                  {p.short}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {visibleSections.length > 0 && (
            <Tooltip title="Обновить данные">
              <span>
                <IconButton onClick={refresh} disabled={isFetching} aria-label="Обновить данные">
                  {isFetching ? <CircularProgress size={20} /> : <MdRefresh size={22} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </div>
      </header>

      {visibleSections.length === 0 && (
        <div className="dashboard__empty">
          <MdOutlineInsights size={48} />
          <div className="dashboard__empty-title">Аналитика пока недоступна</div>
          <div>Для вашей организации не подключены модули с аналитикой. Обратитесь к администратору.</div>
        </div>
      )}

      {visibleSections.map((section) => (
        <section key={section} className="dashboard__section">
          {visibleSections.length > 1 && (
            <div className="dashboard-section-head">
              <h6 className="dashboard-section-title">{SECTION_TITLES[section]}</h6>
              {SECTION_HINTS[section] && <span className="dashboard-section-hint">{SECTION_HINTS[section]}</span>}
            </div>
          )}
          <div className={`dashboard-grid dashboard-grid--${section}`}>
            {bySection[section].map((w) => (
              <div
                key={w.id}
                className={section === "kpi" ? "dashboard-grid__item" : `dashboard-grid__item dashboard-grid__item--${w.size || "half"}`}
              >
                {w.render({ month })}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default StatsPage;
